import { useState, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Upload, Check, ArrowRight, ArrowLeft, Database, Sparkles, Shield, Palette } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'theme_blue', name: 'Bleu Premium', colors: { bg: '#0a1628', primary: '#00b4d8' } },
  { id: 'coinbase', name: 'Coinbase', colors: { bg: '#0a0f14', primary: '#0052ff' } },
  { id: 'revolut', name: 'Revolut', colors: { bg: '#0a0f14', primary: '#00d4aa' } },
  { id: 'binance', name: 'Binance', colors: { bg: '#0b0e11', primary: '#f0b90b' } },
  { id: 'kraken', name: 'Kraken', colors: { bg: '#0f0f1a', primary: '#7b61ff' } },
  { id: 'theme_light', name: 'Vert Premium', colors: { bg: '#fcfcfc', primary: '#01C38D' } },
];

export default function SetupWizard({ onComplete }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [importStatus, setImportStatus] = useState({ data: null, media: null });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [pin, setPin] = useState('');
  const [platformName, setPlatformName] = useState('');
  const [templateId, setTemplateId] = useState('theme_blue');

  const handleImport = useCallback(async (file, type) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/setup/restore`, formData);
      setImportStatus(prev => ({ ...prev, [type]: res.data }));
      toast.success(`${type === 'data' ? 'Données' : 'Média'} importé : ${res.data.documents_restored} éléments`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Erreur import ${type}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFinishSetup = async () => {
    if (!email || !password || !firstName) { toast.error('Remplissez tous les champs'); return; }
    if (password.length < 6) { toast.error('Mot de passe trop court (min 6)'); return; }
    if (password !== confirmPassword) { toast.error('Les mots de passe ne correspondent pas'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/setup/init`, {
        email, password, first_name: firstName, last_name: lastName,
        pin, platform_name: platformName, template_id: templateId,
      });
      toast.success('Plateforme configurée !');
      setTimeout(() => onComplete(), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const progressSteps = ['Compte', 'PIN', 'Nom', 'Design'];
  const progressIndex = step >= 2 ? step - 2 : -1;

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#0a1628' }}>
      <Card className="w-full max-w-lg bg-card/95 backdrop-blur" data-testid="setup-wizard">
        <CardContent className="p-6 sm:p-8">

          {/* Progress bar for fresh install */}
          {step >= 2 && step <= 5 && (
            <div className="flex items-center gap-1 mb-6">
              {progressSteps.map((label, i) => (
                <div key={label} className="flex-1">
                  <div className={`h-1 rounded-full transition-colors ${i <= progressIndex ? 'bg-primary' : 'bg-border'}`} />
                  <p className={`text-[10px] mt-1 text-center ${i <= progressIndex ? 'text-primary' : 'text-muted-foreground'}`}>{label}</p>
                </div>
              ))}
            </div>
          )}

          {/* ========== STEP 0: CHOICE ========== */}
          {step === 0 && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <Sparkles className="w-12 h-12 mx-auto text-primary" />
                <h1 className="text-2xl font-bold">Bienvenue</h1>
                <p className="text-muted-foreground">Configurez votre plateforme en quelques étapes</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button onClick={() => setStep(2)} className="p-6 rounded-xl border-2 border-border hover:border-primary/50 transition-all text-left space-y-3" data-testid="setup-fresh">
                  <Sparkles className="w-8 h-8 text-primary" />
                  <p className="font-semibold text-lg">Nouvelle installation</p>
                  <p className="text-sm text-muted-foreground">Démarrer de zéro avec un nouveau compte</p>
                </button>
                <button onClick={() => setStep(1)} className="p-6 rounded-xl border-2 border-border hover:border-primary/50 transition-all text-left space-y-3" data-testid="setup-import">
                  <Database className="w-8 h-8 text-primary" />
                  <p className="font-semibold text-lg">Importer une sauvegarde</p>
                  <p className="text-sm text-muted-foreground">Restaurer depuis un fichier Data et/ou Media</p>
                </button>
              </div>
            </div>
          )}

          {/* ========== STEP 1: IMPORT ========== */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <Database className="w-12 h-12 mx-auto text-primary" />
                <h1 className="text-2xl font-bold">Importer une sauvegarde</h1>
                <p className="text-muted-foreground">Importez vos fichiers Data et/ou Media</p>
              </div>

              <div className="p-4 rounded-lg border space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">Fichier Media (design)</p>
                    <p className="text-xs text-muted-foreground">Logo, thème, vidéo, paramètres</p>
                  </div>
                  {importStatus.media && <Check className="w-5 h-5 text-green-500" />}
                </div>
                <input type="file" id="wiz-media" className="hidden" accept=".gz,.json"
                  onChange={(e) => { if (e.target.files?.[0]) handleImport(e.target.files[0], 'media'); }} />
                <Button variant="outline" className="w-full" disabled={loading || !!importStatus.media}
                  onClick={() => document.getElementById('wiz-media').click()} data-testid="import-media-btn">
                  <Upload className="w-4 h-4 mr-2" />{importStatus.media ? 'Media importé' : 'Choisir le fichier Media'}
                </Button>
              </div>

              <div className="p-4 rounded-lg border space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">Fichier Data (données)</p>
                    <p className="text-xs text-muted-foreground">Clients, transactions, bureaux...</p>
                  </div>
                  {importStatus.data && <Check className="w-5 h-5 text-green-500" />}
                </div>
                <input type="file" id="wiz-data" className="hidden" accept=".gz,.json"
                  onChange={(e) => { if (e.target.files?.[0]) handleImport(e.target.files[0], 'data'); }} />
                <Button variant="outline" className="w-full" disabled={loading || !!importStatus.data}
                  onClick={() => document.getElementById('wiz-data').click()} data-testid="import-data-btn">
                  <Upload className="w-4 h-4 mr-2" />{importStatus.data ? 'Data importé' : 'Choisir le fichier Data'}
                </Button>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setStep(0); setImportStatus({ data: null, media: null }); }}>
                  <ArrowLeft className="w-4 h-4 mr-2" />Retour
                </Button>
                <Button className="flex-1" disabled={!importStatus.data && !importStatus.media}
                  onClick={() => { toast.success('Import terminé !'); onComplete(); }} data-testid="import-finish-btn">
                  Terminer <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Importez Media pour le design, Data pour les comptes et données. Les deux sont recommandés.
              </p>
            </div>
          )}

          {/* ========== STEP 2: ADMIN ACCOUNT ========== */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <Shield className="w-12 h-12 mx-auto text-primary" />
                <h1 className="text-2xl font-bold">Compte Super Admin</h1>
                <p className="text-muted-foreground">Créez le compte administrateur principal</p>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Prénom</Label>
                    <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jean" data-testid="setup-firstname" />
                  </div>
                  <div className="space-y-1">
                    <Label>Nom</Label>
                    <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Dupont" data-testid="setup-lastname" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@example.com" data-testid="setup-email" />
                </div>
                <div className="space-y-1">
                  <Label>Mot de passe (min 6 caractères)</Label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe sécurisé" data-testid="setup-password" />
                </div>
                <div className="space-y-1">
                  <Label>Confirmer le mot de passe</Label>
                  <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Retapez le mot de passe" />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="w-4 h-4 mr-2" />Retour</Button>
                <Button className="flex-1" onClick={() => {
                  if (!email || !password || !firstName) { toast.error('Remplissez les champs obligatoires'); return; }
                  if (password.length < 6) { toast.error('Mot de passe trop court'); return; }
                  if (password !== confirmPassword) { toast.error('Mots de passe différents'); return; }
                  setStep(3);
                }} data-testid="setup-next-admin">Suivant <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </div>
          )}

          {/* ========== STEP 3: PIN ========== */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <Shield className="w-12 h-12 mx-auto text-primary" />
                <h1 className="text-2xl font-bold">Code PIN</h1>
                <p className="text-muted-foreground">Code PIN d'accès à la plateforme (optionnel)</p>
              </div>
              <div className="space-y-2">
                <Label>Code PIN (4-6 chiffres)</Label>
                <Input type="text" inputMode="numeric" maxLength={6} value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456" className="text-center text-2xl tracking-[0.5em] font-mono" data-testid="setup-pin" />
                <p className="text-xs text-muted-foreground text-center">Laissez vide pour désactiver le PIN.</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-2" />Retour</Button>
                <Button className="flex-1" onClick={() => setStep(4)} data-testid="setup-next-pin">Suivant <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </div>
          )}

          {/* ========== STEP 4: PLATFORM NAME ========== */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <Sparkles className="w-12 h-12 mx-auto text-primary" />
                <h1 className="text-2xl font-bold">Nom de la plateforme</h1>
                <p className="text-muted-foreground">Comment s'appelle votre plateforme ?</p>
              </div>
              <div className="space-y-2">
                <Label>Nom (affiché partout)</Label>
                <Input value={platformName} onChange={(e) => setPlatformName(e.target.value)}
                  placeholder="Ma Plateforme" className="text-lg h-12" data-testid="setup-platform-name" />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft className="w-4 h-4 mr-2" />Retour</Button>
                <Button className="flex-1" onClick={() => setStep(5)} data-testid="setup-next-platform">Suivant <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </div>
          )}

          {/* ========== STEP 5: TEMPLATE ========== */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <Palette className="w-12 h-12 mx-auto text-primary" />
                <h1 className="text-2xl font-bold">Choisir le design</h1>
                <p className="text-muted-foreground">Modifiable ensuite dans Personnalisation</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {TEMPLATES.map(tpl => (
                  <button key={tpl.id} onClick={() => setTemplateId(tpl.id)}
                    className={`p-4 rounded-lg border-2 transition-all ${templateId === tpl.id ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/30'}`}>
                    <div className="h-16 rounded mb-2 flex items-center justify-center" style={{ backgroundColor: tpl.colors.bg }}>
                      <div className="w-8 h-2 rounded" style={{ backgroundColor: tpl.colors.primary }} />
                    </div>
                    <p className="text-sm font-medium">{tpl.name}</p>
                    {templateId === tpl.id && <Check className="w-4 h-4 text-primary mx-auto mt-1" />}
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(4)}><ArrowLeft className="w-4 h-4 mr-2" />Retour</Button>
                <Button className="flex-1" onClick={handleFinishSetup} disabled={loading} data-testid="setup-finish">
                  {loading ? 'Création...' : 'Terminer'} <Check className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* ========== STEP 6: DONE ========== */}
          {step === 6 && (
            <div className="text-center space-y-4 py-8">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold">Configuration terminée !</h1>
              <p className="text-muted-foreground">Redirection vers la connexion...</p>
            </div>
          )}

        </CardContent>
      </Card>
    </div>
  );
}
